import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { subAffDealsV2Service } from "../../../services/admin/subAffDealsV2.service";
import { OrderType } from "../../../components/affiliate/Trackers";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";

export type payloadSubAffDealsV2List = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter: {
    search: string | null;
  };
};

export type listItem = {
  id: number;
  title: string;
  description: string;
  read_only: boolean;
  created_at: string;
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export interface ISubAffDealsV2ListData {
  list: listItem[];
	columns: columnsItem[];
	startRowNum: number;
	endRowNum: number;
	totalEntries: number | string;
	totalPages: number;
};


export type subAffDealsV2SliceState = {
  list: {
    data: ISubAffDealsV2ListData | null,
    error: any,
    isFetching: boolean,
  },
  deleteCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  constants: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  createCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  viewCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  editCondition: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
	filterCreator: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  deal: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  dealLevels: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  dealList: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  updateAssignableDeal: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  history: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  updateHistoryRow: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  deleteHistoryRow: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  dealOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  refProgramStatus: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  dealParent: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  initialSubAffDeal: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: subAffDealsV2SliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  deleteCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
  constants: {
    data: null,
    error: null,
    isFetching: false,
  },
  createCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
  viewCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
  editCondition: {
    data: null,
    error: null,
    isFetching: false,
  },
	filterCreator: {
    data: null,
    error: null,
    isFetching: false,
  },
  deal: {
    data: null,
    error: null,
    isFetching: false,
  },
  dealLevels: {
    data: null,
    error: null,
    isFetching: false,
  },
  dealList: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateAssignableDeal: {
    data: null,
    error: null,
    isFetching: false,
  },
  history: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateHistoryRow: {
    data: null,
    error: null,
    isFetching: false,
  },
  deleteHistoryRow: {
    data: null,
    error: null,
    isFetching: false,
  },
  dealOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  refProgramStatus: {
    data: null,
    error: null,
    isFetching: false,
  },
  dealParent: {
    data: null,
    error: null,
    isFetching: false,
  },
  initialSubAffDeal: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'subAffDealsV2/getListData',
  async (payload: payloadSubAffDealsV2List, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const deleteConditionData: any = createAsyncThunk(
  'subAffDealsV2/deleteCondition',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.deleteCondition(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 200) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.removed',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getConstantsData: any = createAsyncThunk(
  'subAffDealsV2/getConstantsData',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.getConstants();
      const data = await response.json();
      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const createConditionData: any = createAsyncThunk(
  'subAffDealsV2/createConditionData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.createCondition(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getViewConditionData: any = createAsyncThunk(
  'subAffDealsV2/getViewConditionData',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getCondition(id);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue({
          status: response.status,
          statusText: response.statusText,
          data: data,
        })
      }
      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const editConditionData: any = createAsyncThunk(
  'subAffDealsV2/editConditionData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.editCondition(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getFilterCreatorData: any = createAsyncThunk(
	'subAffDealsV2/getFilterCreatorData',
	async (payload: any, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await subAffDealsV2Service.getFilterCreator(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const getSubAffDealData: any = createAsyncThunk(
  'subAffDealsV2/getSubAffDealData',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getSubAffDeal(id);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue({
          status: response.status,
          statusText: response.statusText,
          data: data,
        })
      }
      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getSubAffDealLevelsData: any = createAsyncThunk(
  'subAffDealsV2/getSubAffDealLevelsData',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getSubAffDealLevels(id);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue({
          status: response.status,
          statusText: response.statusText,
          data: data,
        })
      }
      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getSubAffDealListData: any = createAsyncThunk(
  'subAffDealsV2/getSubAffDealListData',
  async (payload: payloadSubAffDealsV2List, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getSubAffDealList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateSubAffDealData: any = createAsyncThunk(
  'subAffDealsV2/updateSubAffDealData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.updateSubAffDeal(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getHistoryData: any = createAsyncThunk(
  'subAffDealsV2/getHistoryData',
  async (payload: payloadSubAffDealsV2List, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getHistory(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateHistoryRowData: any = createAsyncThunk(
  'subAffDealsV2/updateHistoryRowData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.updateHistoryRow(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const deleteHistoryRowData: any = createAsyncThunk(
  'subAffDealsV2/deleteHistoryRowData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.deleteHistoryRow(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getSubAffDealOptionsListData: any = createAsyncThunk(
  'subAffDealsV2/getSubAffDealOptionsListData',
  async (payload: payloadSubAffDealsV2List, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getSubAffDealOptionsList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateRefProgramStatusData: any = createAsyncThunk(
  'subAffDealsV2/updateRefProgramStatusData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.updateRefProgramStatus(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getParentSubAffDealData: any = createAsyncThunk(
  'subAffDealsV2/getParentSubAffDealData',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffDealsV2Service.getParentSubAffDeal(id);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue({
          status: response.status,
          statusText: response.statusText,
          data: data,
        })
      }
      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createInitialSubAffDealData: any = createAsyncThunk(
  'subAffDealsV2/createInitialSubAffDealData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffDealsV2Service.createInitialSubAffDeal(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const subAffDealsV2Slice = createSlice({
  name: 'subAffDealsV2',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearDeleteConditionState(state) {
      state.deleteCondition.error = null;
      state.deleteCondition.isFetching = false;
      state.deleteCondition.data = null;
    },
    clearCreateConditionState(state) {
      state.createCondition.error = null;
      state.createCondition.isFetching = false;
      state.createCondition.data = null;
    },
    clearViewConditionState(state) {
      state.viewCondition.error = null;
      state.viewCondition.isFetching = false;
      state.viewCondition.data = null;
    },
    clearConstantsState(state) {
      state.constants.error = null;
      state.constants.isFetching = false;
      state.constants.data = null;
    },
    clearEditConditionState(state) {
      state.editCondition.error = null;
      state.editCondition.isFetching = false;
      state.editCondition.data = null;
    },
    clearSubAffDealState(state) {
      state.deal.error = null;
      state.deal.isFetching = false;
      state.deal.data = null;
    },
    clearSubAffDealLevelsState(state) {
      state.dealLevels.error = null;
      state.dealLevels.isFetching = false;
      state.dealLevels.data = null;
    },
    clearSubAffDealListState(state) {
      state.dealList.error = null;
      state.dealList.isFetching = false;
      state.dealList.data = null;
    },
    clearUpdateSubAffDealState(state) {
      state.updateAssignableDeal.error = null;
      state.updateAssignableDeal.isFetching = false;
      state.updateAssignableDeal.data = null;
    },
    clearHistoryState(state) {
      state.history.error = null;
      state.history.isFetching = false;
      state.history.data = null;
    },
    clearDeleteHistoryRowState(state) {
      state.deleteHistoryRow.error = null;
      state.deleteHistoryRow.isFetching = false;
      state.deleteHistoryRow.data = null;
    },
    clearUpdateHistoryRowState(state) {
      state.updateHistoryRow.error = null;
      state.updateHistoryRow.isFetching = false;
      state.updateHistoryRow.data = null;
    },
    clearSubAffDealOptionsListState(state) {
      state.dealOptions.error = null;
      state.dealOptions.isFetching = false;
      state.dealOptions.data = null;
    },
    clearUpdateRefProgramStatusState(state) {
      state.refProgramStatus.error = null;
      state.refProgramStatus.isFetching = false;
      state.refProgramStatus.data = null;
    },
    clearInitialSubAffDealState(state) {
      state.initialSubAffDeal.error = null;
      state.initialSubAffDeal.isFetching = false;
      state.initialSubAffDeal.data = null;
    },
    clearParentSubAffDealState(state) {
      state.dealParent.error = null;
      state.dealParent.isFetching = false;
      state.dealParent.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<subAffDealsV2SliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(deleteConditionData.pending, (state ) => {
      state.deleteCondition.isFetching = true;
      state.deleteCondition.error = null;
    });
    builder.addCase(deleteConditionData.fulfilled, (state , action) => {
      state.deleteCondition.error = null;
      state.deleteCondition.isFetching = false;
      state.deleteCondition.data = action.payload;
    });
    builder.addCase(deleteConditionData.rejected, (state , action) => {
      state.deleteCondition.isFetching = false;
      state.deleteCondition.error = action.payload;
      state.deleteCondition.data = null;
    });

    builder.addCase(getConstantsData.pending, (state ) => {
      state.constants.isFetching = true;
      state.constants.error = null;
    });
    builder.addCase(getConstantsData.fulfilled, (state , action) => {
      state.constants.error = null;
      state.constants.isFetching = false;
      state.constants.data = action.payload;
    });
    builder.addCase(getConstantsData.rejected, (state , action) => {
      state.constants.isFetching = false;
      state.constants.error = action.payload;
      state.constants.data = null;
    });

    builder.addCase(createConditionData.pending, (state ) => {
      state.createCondition.isFetching = true;
      state.createCondition.error = null;
    });
    builder.addCase(createConditionData.fulfilled, (state , action) => {
      state.createCondition.error = null;
      state.createCondition.isFetching = false;
      state.createCondition.data = action.payload;
    });
    builder.addCase(createConditionData.rejected, (state , action) => {
      state.createCondition.isFetching = false;
      state.createCondition.error = action.payload;
      state.createCondition.data = null;
    });

    builder.addCase(getViewConditionData.pending, (state ) => {
      state.viewCondition.isFetching = true;
      state.viewCondition.error = null;
    });
    builder.addCase(getViewConditionData.fulfilled, (state , action) => {
      state.viewCondition.error = null;
      state.viewCondition.isFetching = false;
      state.viewCondition.data = action.payload;
    });
    builder.addCase(getViewConditionData.rejected, (state , action) => {
      state.viewCondition.isFetching = false;
      state.viewCondition.error = action.payload;
      state.viewCondition.data = null;
    });

    builder.addCase(editConditionData.pending, (state ) => {
      state.editCondition.isFetching = true;
      state.editCondition.error = null;
    });
    builder.addCase(editConditionData.fulfilled, (state , action) => {
      state.editCondition.error = null;
      state.editCondition.isFetching = false;
      state.editCondition.data = action.payload;
    });
    builder.addCase(editConditionData.rejected, (state , action) => {
      state.editCondition.isFetching = false;
      state.editCondition.error = action.payload;
      state.editCondition.data = null;
    });

		builder.addCase(getFilterCreatorData.pending, (state ) => {
      state.filterCreator.isFetching = true;
      state.filterCreator.error = null;
    });
    builder.addCase(getFilterCreatorData.fulfilled, (state , action) => {
      state.filterCreator.error = null;
      state.filterCreator.isFetching = false;
      state.filterCreator.data = action.payload;
    });
    builder.addCase(getFilterCreatorData.rejected, (state , action) => {
      state.filterCreator.isFetching = false;
      state.filterCreator.error = action.payload;
      state.filterCreator.data = null;
    });

    builder.addCase(getSubAffDealData.pending, (state ) => {
      state.deal.isFetching = true;
      state.deal.error = null;
    });
    builder.addCase(getSubAffDealData.fulfilled, (state , action) => {
      state.deal.error = null;
      state.deal.isFetching = false;
      state.deal.data = action.payload;
    });
    builder.addCase(getSubAffDealData.rejected, (state , action) => {
      state.deal.isFetching = false;
      state.deal.error = action.payload;
      state.deal.data = null;
    });

    builder.addCase(getSubAffDealLevelsData.pending, (state ) => {
      state.dealLevels.isFetching = true;
      state.dealLevels.error = null;
    });
    builder.addCase(getSubAffDealLevelsData.fulfilled, (state , action) => {
      state.dealLevels.error = null;
      state.dealLevels.isFetching = false;
      state.dealLevels.data = action.payload;
    });
    builder.addCase(getSubAffDealLevelsData.rejected, (state , action) => {
      state.dealLevels.isFetching = false;
      state.dealLevels.error = action.payload;
      state.dealLevels.data = null;
    });

    builder.addCase(getSubAffDealListData.pending, (state ) => {
      state.dealList.isFetching = true;
      state.dealList.error = null;
    });
    builder.addCase(getSubAffDealListData.fulfilled, (state , action) => {
      state.dealList.error = null;
      state.dealList.isFetching = false;
      state.dealList.data = action.payload;
    });
    builder.addCase(getSubAffDealListData.rejected, (state , action) => {
      state.dealList.isFetching = false;
      state.dealList.error = action.payload;
      state.dealList.data = null;
    });

    builder.addCase(updateSubAffDealData.pending, (state ) => {
      state.updateAssignableDeal.isFetching = true;
      state.updateAssignableDeal.error = null;
    });
    builder.addCase(updateSubAffDealData.fulfilled, (state , action) => {
      state.updateAssignableDeal.error = null;
      state.updateAssignableDeal.isFetching = false;
      state.updateAssignableDeal.data = action.payload;
    });
    builder.addCase(updateSubAffDealData.rejected, (state , action) => {
      state.updateAssignableDeal.isFetching = false;
      state.updateAssignableDeal.error = action.payload;
      state.updateAssignableDeal.data = null;
    });

    builder.addCase(getHistoryData.pending, (state ) => {
      state.history.isFetching = true;
      state.history.error = null;
    });
    builder.addCase(getHistoryData.fulfilled, (state , action) => {
      state.history.error = null;
      state.history.isFetching = false;
      state.history.data = action.payload;
    });
    builder.addCase(getHistoryData.rejected, (state , action) => {
      state.history.isFetching = false;
      state.history.error = action.payload;
      state.history.data = null;
    });

    builder.addCase(updateHistoryRowData.pending, (state ) => {
      state.updateHistoryRow.isFetching = true;
      state.updateHistoryRow.error = null;
    });
    builder.addCase(updateHistoryRowData.fulfilled, (state , action) => {
      state.updateHistoryRow.error = null;
      state.updateHistoryRow.isFetching = false;
      state.updateHistoryRow.data = action.payload;
    });
    builder.addCase(updateHistoryRowData.rejected, (state , action) => {
      state.updateHistoryRow.isFetching = false;
      state.updateHistoryRow.error = action.payload;
      state.updateHistoryRow.data = null;
    });

    builder.addCase(deleteHistoryRowData.pending, (state ) => {
      state.deleteHistoryRow.isFetching = true;
      state.deleteHistoryRow.error = null;
    });
    builder.addCase(deleteHistoryRowData.fulfilled, (state , action) => {
      state.deleteHistoryRow.error = null;
      state.deleteHistoryRow.isFetching = false;
      state.deleteHistoryRow.data = action.payload;
    });
    builder.addCase(deleteHistoryRowData.rejected, (state , action) => {
      state.deleteHistoryRow.isFetching = false;
      state.deleteHistoryRow.error = action.payload;
      state.deleteHistoryRow.data = null;
    });

    builder.addCase(getSubAffDealOptionsListData.pending, (state ) => {
      state.dealOptions.isFetching = true;
      state.dealOptions.error = null;
    });
    builder.addCase(getSubAffDealOptionsListData.fulfilled, (state , action) => {
      state.dealOptions.error = null;
      state.dealOptions.isFetching = false;
      state.dealOptions.data = action.payload;
    });
    builder.addCase(getSubAffDealOptionsListData.rejected, (state , action) => {
      state.dealOptions.isFetching = false;
      state.dealOptions.error = action.payload;
      state.dealOptions.data = null;
    });

    builder.addCase(updateRefProgramStatusData.pending, (state ) => {
      state.refProgramStatus.isFetching = true;
      state.refProgramStatus.error = null;
    });
    builder.addCase(updateRefProgramStatusData.fulfilled, (state , action) => {
      state.refProgramStatus.error = null;
      state.refProgramStatus.isFetching = false;
      state.refProgramStatus.data = action.payload;
    });
    builder.addCase(updateRefProgramStatusData.rejected, (state , action) => {
      state.refProgramStatus.isFetching = false;
      state.refProgramStatus.error = action.payload;
      state.refProgramStatus.data = null;
    });

    builder.addCase(getParentSubAffDealData.pending, (state ) => {
      state.dealParent.isFetching = true;
      state.dealParent.error = null;
    });
    builder.addCase(getParentSubAffDealData.fulfilled, (state , action) => {
      state.dealParent.error = null;
      state.dealParent.isFetching = false;
      state.dealParent.data = action.payload;
    });
    builder.addCase(getParentSubAffDealData.rejected, (state , action) => {
      state.dealParent.isFetching = false;
      state.dealParent.error = action.payload;
      state.dealParent.data = null;
    });

    builder.addCase(createInitialSubAffDealData.pending, (state ) => {
      state.initialSubAffDeal.isFetching = true;
      state.initialSubAffDeal.error = null;
    });
    builder.addCase(createInitialSubAffDealData.fulfilled, (state , action) => {
      state.initialSubAffDeal.error = null;
      state.initialSubAffDeal.isFetching = false;
      state.initialSubAffDeal.data = action.payload;
    });
    builder.addCase(createInitialSubAffDealData.rejected, (state , action) => {
      state.initialSubAffDeal.isFetching = false;
      state.initialSubAffDeal.error = action.payload;
      state.initialSubAffDeal.data = null;
    });
  },
});

export const {
  clearListState,
  clearDeleteConditionState,
  clearCreateConditionState,
  clearViewConditionState,
  clearConstantsState,
  clearEditConditionState,
  clearSubAffDealState,
  clearSubAffDealLevelsState,
  clearSubAffDealListState,
  clearUpdateSubAffDealState,
  clearHistoryState,
  clearDeleteHistoryRowState,
  clearUpdateHistoryRowState,
  clearSubAffDealOptionsListState,
  clearUpdateRefProgramStatusState,
  clearInitialSubAffDealState,
  clearParentSubAffDealState,
} = subAffDealsV2Slice.actions;

export default subAffDealsV2Slice.reducer;
